import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import VoiceLogo from "@/components/VoiceLogo";
import { Loader2, ArrowLeft, Phone, Mail, Lock, User, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: phone, 2: verify code, 3: account details
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [devCode, setDevCode] = useState(""); // For development only

  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      setError("Please enter a valid phone number");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/send-verification`, { phone });
      // In development, the API returns the code for testing
      if (response.data.dev_code) {
        setDevCode(response.data.dev_code);
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/verify-phone`, { phone, code: verificationCode });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!email || !password || !name) {
      setError("Please fill in all fields");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Create the account
      await axios.post(`${API}/auth/signup`, { email, password, name }, { withCredentials: true });
      
      // Update user with verified phone
      // Note: In production, you'd save the phone number during signup
      
      // Navigate to the download page
      navigate('/download');
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create account");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0f1419' }}>
      {/* Header */}
      <header className="p-4" style={{ borderBottom: '1px solid #2d3748' }}>
        <div className="max-w-md mx-auto flex items-center gap-4">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/')} className="p-2 rounded-full hover:bg-gray-800">
            <ArrowLeft className="h-5 w-5" style={{ color: '#a0aec0' }} />
          </button>
          <div className="flex items-center gap-2">
            <VoiceLogo size={28} />
            <span className="text-lg font-semibold text-white">Voice Capture</span>
          </div>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="max-w-md mx-auto w-full px-4 pt-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`h-2 rounded-full transition-all ${s === step ? 'w-8' : 'w-2'}`}
              style={{ backgroundColor: s <= step ? '#6366f1' : '#2d3748' }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 pt-8">
        <div className="w-full max-w-md">
          {/* Step 1: Phone Number */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6366f120' }}>
                  <Phone className="h-8 w-8" style={{ color: '#6366f1' }} />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Verify Your Phone</h1>
                <p className="text-sm" style={{ color: '#a0aec0' }}>
                  We will send you a verification code to get started with your free trial.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-white">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setError(""); }}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-3 rounded-xl text-white"
                  style={{ backgroundColor: '#1a2332', border: error ? '1px solid #ef4444' : '1px solid #2d3748' }}
                />
                {error && <p className="text-sm mt-2" style={{ color: '#ef4444' }}>{error}</p>}
              </div>

              <Button
                onClick={handleSendCode}
                disabled={isLoading}
                className="w-full py-6 rounded-xl text-lg font-semibold"
                style={{ backgroundColor: '#6366f1' }}
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                Send Verification Code
              </Button>

              <p className="text-center text-xs" style={{ color: '#718096' }}>
                By continuing, you agree to our{" "}
                <button onClick={() => navigate('/terms')} className="underline hover:text-white">
                  Terms & Conditions
                </button>
              </p>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: '#22c55e' }} />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Enter Code</h1>
                <p className="text-sm" style={{ color: '#a0aec0' }}>
                  We sent a 6-digit code to <strong>{phone}</strong>
                </p>
              </div>

              {/* Dev code display - remove in production */}
              {devCode && (
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#f59e0b20', border: '1px solid #f59e0b' }}>
                  <p className="text-xs" style={{ color: '#f59e0b' }}>Development Mode - Your code is: <strong>{devCode}</strong></p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2 text-white">Verification Code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => { setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(""); }}
                  placeholder="000000"
                  className="w-full px-4 py-3 rounded-xl text-white text-center text-2xl tracking-widest"
                  style={{ backgroundColor: '#1a2332', border: error ? '1px solid #ef4444' : '1px solid #2d3748' }}
                  maxLength={6}
                />
                {error && <p className="text-sm mt-2 text-center" style={{ color: '#ef4444' }}>{error}</p>}
              </div>

              <Button
                onClick={handleVerifyCode}
                disabled={isLoading || verificationCode.length !== 6}
                className="w-full py-6 rounded-xl text-lg font-semibold"
                style={{ backgroundColor: '#6366f1' }}
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                Verify Code
              </Button>

              <button 
                onClick={() => handleSendCode()}
                className="w-full text-center text-sm hover:underline"
                style={{ color: '#6366f1' }}
              >
                Resend Code
              </button>
            </div>
          )}

          {/* Step 3: Create Account */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6366f120' }}>
                  <User className="h-8 w-8" style={{ color: '#6366f1' }} />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Create Your Account</h1>
                <p className="text-sm" style={{ color: '#a0aec0' }}>
                  Almost there! Set up your account to start your free trial.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setError(""); }}
                      placeholder="John Doe"
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-white"
                      style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-white"
                      style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: '#718096' }} />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(""); }}
                      placeholder="At least 6 characters"
                      className="w-full pl-10 pr-4 py-3 rounded-xl text-white"
                      style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
                    />
                  </div>
                </div>

                {error && <p className="text-sm text-center" style={{ color: '#ef4444' }}>{error}</p>}
              </div>

              <Button
                onClick={handleCreateAccount}
                disabled={isLoading}
                className="w-full py-6 rounded-xl text-lg font-semibold"
                style={{ backgroundColor: '#6366f1' }}
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
                Start Free Trial
              </Button>

              {/* Free trial info */}
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#22c55e10', border: '1px solid #22c55e30' }}>
                <p className="text-center text-sm" style={{ color: '#22c55e' }}>
                  🎉 You get <strong>10 minutes FREE</strong> to try all features. No credit card required!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
